import React, { useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Collapse,
  IconButton,
  Typography,
} from "@mui/material";
import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import axios from "axios";
import { AUDIT_CALLING_DETAILS_REPORT } from "../../constants";
import CallIcon from "@mui/icons-material/Call";

function MissedCallTable({ callStatistics, filters, setMetricData, setLoading }) {
  const empDetails = JSON.parse(localStorage.getItem("userData"));
  const navigate = useNavigate();
  const [page, setPage] = useState(0); // Current page
  const [rowsPerPage, setRowsPerPage] = useState(10); // Rows per page
  const [totalCount, setTotalCount] = useState(0);
  const [openRows, setOpenRows] = useState([]); // To manage which rows are expanded

  const { agentName, agentNo } = useParams();

  const [callData, setCallData] = useState([]);

  const fetchConnectedData = () => {
    const { startDate, endDate } = filters;

    // Determine yesterday's date
    const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");

    // Format startDate and endDate, defaulting to yesterday if not provided
    const formattedStartDate = startDate
      ? dayjs(startDate).format("YYYY-MM-DD")
      : yesterday;
    const formattedEndDate = endDate
      ? dayjs(endDate).format("YYYY-MM-DD")
      : yesterday;

    // Build query params
    const queryParams = new URLSearchParams();
    queryParams.append("startDate", formattedStartDate);
    queryParams.append("endDate", formattedEndDate);
    if (localStorage.getItem("empRole") === "100") {
      queryParams.append("agentNumber", empDetails.EmployeePhone);
    } else {
      queryParams.append("agentNumber", agentNo);
    }
    queryParams.append("callStatus", "Missed");
    queryParams.append("page", page + 1); // Current page (1-based index)
    queryParams.append("pageSize", rowsPerPage); // Number of rows per page
    setLoading(true);
    axios
      .get(`${AUDIT_CALLING_DETAILS_REPORT}?${queryParams.toString()}`)
      .then((response) => {
        const { calls, pagination, summary } = response.data;
        // setMetricData(summary);
        setCallData(calls); // Update the call data
        setTotalCount(pagination.totalRecords); // Update the total record count
        setRowsPerPage(pagination.pageSize); // Update rows per page
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching connected data:", error);
        setCallData([]); // Set call data to empty on error
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchConnectedData();
  }, [filters, page, rowsPerPage]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page
  };

  // Handle row expansion toggle
  const handleRowToggle = (index) => {
    setOpenRows((prev) => {
      if (prev.includes(index)) {
        return prev.filter((rowIndex) => rowIndex !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const formatDate = (dateString) =>
    moment(dateString).format("DD-MM-YYYY HH:mm:ss");

  const IconCell = ({ row }) => (
    <>
      <IconButton
        style={{
          border: "1px solid",
          borderRadius: "4px",
          padding: "2px",
          width: "24px",
          height: "24px",
          justifyContent: "center",
          alignItems: "center",
        }}
        color="primary"
        onClick={() => handleCallIconClick(row)}
      >
        <CallIcon fontSize="small" color="primary" />
      </IconButton>
    </>
  );

  const handleCallIconClick = (row) => {

    navigate("/dashboard", {
      state: {
        mobNo: row.callerNumber,
        lotNum: row.callerNumber,
        missedCallId: row.id
      },
    });
  };

  return (
    <>
      <Paper>
        <TableContainer component={Paper} elevation={3}>
          <Table
            sx={{
              minWidth: 650,
              height: "100%",
              tableLayout: "fixed",
            }}
            aria-label="simple table"
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "#EEF0F4" }}>
                <TableCell width={50} sx={{ textAlign: "center" }} />
                <TableCell
                  width={50}
                  sx={{ fontWeight: "bold", textAlign: "center", padding: 1 }}
                >
                  SNo
                </TableCell>

                <TableCell
                  width={150}
                  sx={{ fontWeight: "bold", textAlign: "center", padding: 1 }}
                >
                  Caller Number
                </TableCell>
                <TableCell
                  width={150}
                  sx={{ fontWeight: "bold", textAlign: "center", padding: 1 }}
                >
                  Start Time
                </TableCell>
                <TableCell
                  width={120}
                  sx={{ fontWeight: "bold", textAlign: "center", padding: 1 }}
                >
                  End Time
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    textAlign: "center",
                    width: "80px",
                    position: "sticky", // Make the cell sticky
                    right: 0, // Fix to the right side
                    backgroundColor: "#EEF0F4", // Set background color for visibility
                    zIndex: 100, // Higher z-index to stay above other cells
                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                    top: 0,
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(callData) && callData.length > 0 ? (
                callData.map((row, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    <TableRow
                      sx={{
                        "&:hover": { backgroundColor: "#f9f9f9" },
                      }}
                    >
                      <TableCell>
                        <IconButton
                          onClick={() => handleRowToggle(rowIndex)}
                          sx={{ padding: 0 }}
                        >
                          <ExpandMoreIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell width={50} sx={{ textAlign: "center" }}>
                        {page * rowsPerPage + rowIndex + 1}
                      </TableCell>
                      {/* <TableCell width={160} sx={{ textAlign: "center" }}>
                          {row.customer_details === null
                            ? "New Customer"
                            : row.customer_details?.farmer_name}
                        </TableCell> */}
                      <TableCell width={150} sx={{ textAlign: "center" }}>
                        {row.callerNumber || "-"}
                      </TableCell>
                      <TableCell width={150} sx={{ textAlign: "center" }}>
                        {row.callStartTime === null
                          ? "-"
                          : formatDate(row.callStartTime)}
                      </TableCell>
                      <TableCell width={120} sx={{ textAlign: "center" }}>
                        {row.callEndTime === null
                          ? "-"
                          : formatDate(row.callEndTime)}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          width: "80px",
                          padding: 1,
                          position: "sticky", // Make the cell sticky
                          right: 0, // Fix to the right side
                          backgroundColor: "#fff", // Set background color for visibility
                          zIndex: 10, // Higher z-index to stay above other cells
                          boxShadow: "0 2px 5px rgba(0,0,0,0.1)", // Add a shadow to the column
                        }}
                      >
                        {localStorage.getItem("empRole") === "100" && (<IconCell row={row} />)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ paddingBottom: 0, paddingTop: 0 }}
                      >
                        <Collapse
                          in={openRows.includes(rowIndex)}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Paper sx={{ padding: 2 }}>
                            <Typography variant="h6">
                              Customer Details
                            </Typography>
                            <Typography>
                              <strong>Farmer Name:</strong>{" "}
                              {row.customer_details?.farmer_name || "-"}
                            </Typography>
                            <Typography>
                              <strong>Lot Number:</strong>{" "}
                              {row.customer_details?.lot_number || "-"}
                            </Typography>
                            <Typography>
                              <strong>Zone:</strong>{" "}
                              {row.customer_details?.zone || "-"}
                            </Typography>
                            <Typography>
                              <strong>Branch:</strong>{" "}
                              {row.customer_details?.branch || "-"}
                            </Typography>
                            <Typography>
                              <strong>Hatch Date:</strong>{" "}
                              {row.customer_details?.hatch_date || "-"}
                            </Typography>
                            <Typography>
                              <strong>Placed Quantity:</strong>{" "}
                              {row.customer_details?.placed_qty || "-"}
                            </Typography>
                            <Typography>
                              <strong>Shed Type:</strong>{" "}
                              {row.customer_details?.shed_type || "-"}
                            </Typography>
                            <Typography>
                              <strong>Status:</strong>{" "}
                              {row.customer_details?.status || "-"}
                            </Typography>
                            <Typography>
                              <strong>Vendor:</strong>{" "}
                              {row.customer_details?.vendor || "-"}
                            </Typography>
                            <Typography>
                              <strong>Total Mortality:</strong>{" "}
                              {row.customer_details?.total_mortality || "-"}
                            </Typography>
                            <Typography>
                              <strong>Mortality Percentage:</strong>{" "}
                              {row.customer_details?.mortality_percentage ||
                                "-"}
                            </Typography>
                          </Paper>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No details yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]} // Options for rows per page
          component="div"
          count={totalCount} // Total number of rows
          rowsPerPage={rowsPerPage} // Current rows per page
          page={page} // Current page
          onPageChange={handleChangePage} // Handle page change
          onRowsPerPageChange={handleChangeRowsPerPage} // Handle rows per page change
        />
      </Paper>
    </>
  );
}

export default MissedCallTable;
